/**
 * SalesTaxSettings — restaurant owner sales-tax config panel.
 *
 * Shows the owner:
 *   • The auto-detected state sales-tax rate (from address.state)
 *   • The effective rate currently being charged (auto OR override)
 *   • An optional override input for cities with extra local tax
 *   • A yellow "local hotspot" suggestion when the restaurant is in
 *     a major U.S. city with known higher combined rate (NYC,
 *     Chicago, Boston, LA, etc.) and no override is set
 *
 * Talks to GET /api/restaurants/:id/tax (owner-only) on mount and
 * after each save. Override changes are pushed via PUT
 * /api/restaurants/:id (the same endpoint everything else uses).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Percent, FileDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SalesTaxSettings = ({ restaurantId, getAuthHeader }) => {
  const [info, setInfo] = useState(null);
  const [override, setOverride] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Bump to force the loader effect below to re-run after a save.
  const [reloadTick, setReloadTick] = useState(0);

  const handleSave = async (payload) => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API}/restaurants/${restaurantId}`, payload, {
        headers: getAuthHeader ? getAuthHeader() : undefined,
      });
      setReloadTick((n) => n + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/restaurants/${restaurantId}/tax`, {
          headers: getAuthHeader ? getAuthHeader() : undefined,
        });
        if (cancelled) return;
        setInfo(res.data);
        const cur = res.data.override_pct;
        setOverride(cur != null && cur !== '' ? String(cur) : '');
        setError('');
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load tax settings');
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantId, reloadTick]);

  const saveOverride = () => {
    const trimmed = (override || '').toString().trim();
    if (trimmed === '') {
      // Clear the override → fall back to state auto-rate.
      handleSave({ tax_rate_override_pct: null });
      return;
    }
    const num = parseFloat(trimmed);
    if (Number.isNaN(num) || num < 0 || num > 20) {
      setError('Enter a number between 0 and 20');
      return;
    }
    handleSave({ tax_rate_override_pct: num });
  };

  const acceptHotspot = (pct) => {
    setOverride(String(pct));
    handleSave({ tax_rate_override_pct: pct });
  };

  if (!info) {
    return (
      <div className="rounded-2xl border border-slate-200 p-5 bg-white">
        <p className="text-sm text-slate-500">Loading sales tax settings…</p>
      </div>
    );
  }

  const effective = Number(info.rate_pct || 0);
  const auto = Number(info.auto_pct || 0);
  const hasOverride = info.source === 'override';
  const hotspot = info.local_suggestion;

  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-white" data-testid="sales-tax-settings">
      <div className="flex items-center gap-2 mb-1">
        <Percent className="w-4 h-4 text-gold-600" />
        <h3 className="text-base font-bold text-slate-900">Sales Tax</h3>
      </div>
      <p className="text-xs text-slate-500 leading-snug mb-4">
        Tax is applied to the order subtotal only (delivery and tips are never taxed). The rate is detected automatically from your restaurant&apos;s state — set a local override below if your city/county adds a meal tax.
      </p>

      {/* Effective rate display */}
      <div
        className="flex items-center justify-between mb-4 p-3 rounded-xl bg-gold-50 border border-gold-200"
        data-testid="sales-tax-effective"
      >
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-gold-700 font-bold">Current effective rate</p>
          <p className="text-2xl font-extrabold text-slate-900 tabular-nums mt-0.5">
            {effective.toFixed(2)}<span className="text-base text-slate-500 ml-0.5">%</span>
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {hasOverride
              ? `Custom local override — state default would be ${auto.toFixed(2)}%`
              : info.state
                ? `Auto-detected from ${info.state} state base rate`
                : 'Default 0% — set your restaurant address to auto-detect'}
          </p>
        </div>
      </div>

      {/* Hotspot suggestion banner */}
      {hotspot && (
        <div
          className="flex items-start gap-2 mb-4 p-3 rounded-xl bg-amber-50 border border-amber-300"
          data-testid="sales-tax-hotspot"
        >
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-bold text-amber-900">
              {hotspot.city}, {hotspot.state} typically has a higher local rate
            </p>
            <p className="text-[11.5px] text-amber-800 leading-snug mt-0.5">
              Restaurants in {hotspot.city} often charge <strong>{hotspot.suggested_pct.toFixed(2)}%</strong> combined.
              {' '}
              <button
                type="button"
                onClick={() => acceptHotspot(hotspot.suggested_pct)}
                disabled={saving}
                className="underline font-bold text-amber-900 hover:text-amber-700"
                data-testid="sales-tax-accept-hotspot"
              >
                Use {hotspot.suggested_pct.toFixed(2)}%
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Override input */}
      <div className="space-y-2">
        <label className="block text-xs font-semibold text-slate-700">
          Local override (%) — leave blank to use the state default
        </label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="number"
              min="0"
              max="20"
              step="0.001"
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              placeholder={`e.g. ${auto > 0 ? auto.toFixed(2) : '6.25'}`}
              className="w-full px-3 py-2 pr-8 border border-slate-200 rounded-lg focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none text-sm"
              data-testid="sales-tax-override-input"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
          </div>
          <button
            type="button"
            onClick={saveOverride}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-black text-sm font-bold transition-colors"
            data-testid="sales-tax-save-btn"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        {error && (
          <p className="text-[12px] text-rose-600 font-semibold" data-testid="sales-tax-error">{error}</p>
        )}
      </div>

      {/* Monthly tax report CSV — restaurants need this every quarter
          to file state sales-tax returns. Only paid orders are
          included so abandoned-cart rows don't end up in the filing. */}
      <TaxReportExport restaurantId={restaurantId} getAuthHeader={getAuthHeader} />
    </div>
  );
};

const monthsList = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TaxReportExport = ({ restaurantId, getAuthHeader }) => {
  const now = new Date();
  // Default to PREVIOUS month — that's the period most restaurants
  // are filing during any given current month.
  const defaultMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const defaultYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [month, setMonth] = useState(defaultMonth);
  const [year, setYear] = useState(defaultYear);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      const res = await axios.get(
        `${API}/restaurants/${restaurantId}/orders/tax-report.csv`,
        {
          params: { year, month },
          responseType: 'blob',
          headers: getAuthHeader ? getAuthHeader() : undefined,
        },
      );
      // Trigger browser download via temporary <a>
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tax-report-${year}-${String(month).padStart(2, '0')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y -= 1) yearOptions.push(y);

  return (
    <div className="mt-5 pt-5 border-t border-slate-200" data-testid="tax-report-export">
      <div className="flex items-center gap-2 mb-1">
        <FileDown className="w-4 h-4 text-slate-700" />
        <h4 className="text-sm font-bold text-slate-900">Monthly Tax Report</h4>
      </div>
      <p className="text-xs text-slate-500 leading-snug mb-3">
        Download a CSV of every paid order for the selected month, with subtotal, sales tax, tip and total. Hand this to your accountant or upload directly to your state sales-tax filing.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={month}
          onChange={(e) => setMonth(Number(e.target.value))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none"
          data-testid="tax-report-month"
        >
          {monthsList.map((m, i) => (
            <option key={m} value={i + 1}>{m}</option>
          ))}
        </select>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none"
          data-testid="tax-report-year"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-bold transition-colors"
          data-testid="tax-report-download-btn"
        >
          <FileDown className="w-3.5 h-3.5" />
          {downloading ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-[12px] text-rose-600 font-semibold" data-testid="tax-report-error">{error}</p>
      )}
    </div>
  );
};

export default SalesTaxSettings;
